import React, { useState } from 'react';
import { X, Calculator, Loader } from 'lucide-react';
import config from '../config';

const TariffCalculator = ({ isOpen, onClose, language = 'ko' }) => {
  const [distance, setDistance] = useState('');
  const [vehicleType, setVehicleType] = useState('1T');
  const [price, setPrice] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null); // 10km 미만 등 안내문

  const vehicleTypes = ['1T', '2.5T', '5T', '8T', '11T', '18T', '25T'];

  const translations = {
    ko: {
      title: '운송료 계산기',
      distance: '운송거리 (km)',
      vehicleType: '화물차량 종류',
      calculate: '계산하기',
      price: '운송료',
      close: '닫기',
      enterDistance: '운송거리를 입력하세요',
      error: '오류',
      tryAgain: '다시 시도',
      result: '결과',
      roundedDistance: '반올림된 거리',
      minDistanceNotice: '현재 예상 운송료는 10km 이상 600km 이하의 배송구간에 대해서만 산정해드리고 있습니다.'
    },
    en: {
      title: 'Freight Calculator',
      distance: 'Distance (km)',
      vehicleType: 'Vehicle Type',
      calculate: 'Calculate',
      price: 'Price',
      close: 'Close',
      enterDistance: 'Enter distance',
      error: 'Error',
      tryAgain: 'Try Again',
      result: 'Result',
      roundedDistance: 'Rounded Distance',
      minDistanceNotice: 'Estimated freight is currently calculated only for delivery distances of 10 km or more and less than 600 km.'
    }
  };

  const t = translations[language] || translations.ko;

  const handleCalculate = async () => {
    setNotice(null);
    if (!distance || distance <= 0) {
      setError('운송거리를 입력해주세요');
      return;
    }

    const dist = parseFloat(distance);
    if (dist < 10 || dist > 600) {
      setError(null);
      setPrice(null);
      setNotice(t.minDistanceNotice);
      return;
    }

    setLoading(true);
    setError(null);
    setPrice(null);

    try {
      const response = await fetch(`${config.API_BASE_URL}/api/tariff`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          distance: parseFloat(distance),
          vehicle_type: vehicleType,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || '운송료 계산에 실패했습니다');
      }

      setPrice(data);
    } catch (err) {
      setError(err.message || '운송료 계산 중 오류가 발생했습니다');
      console.error('Tariff calculation error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDistance('');
    setVehicleType('1T');
    setPrice(null);
    setError(null);
    setNotice(null);
    onClose();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleCalculate();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-blue-500 to-blue-600 text-white">
          <div className="flex items-center space-x-2">
            <Calculator className="w-6 h-6" />
            <h2 className="text-xl font-bold">{t.title}</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white hover:bg-opacity-20 rounded-full transition-colors"
            aria-label={t.close}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Distance Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t.distance}
            </label>
            <input
              type="number"
              value={distance}
              onChange={(e) => setDistance(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t.enterDistance}
              step="0.1"
              min="0"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
            />
          </div>

          {/* Vehicle Type Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t.vehicleType}
            </label>
            <div className="grid grid-cols-3 gap-3">
              {vehicleTypes.map((type) => (
                <button
                  key={type}
                  onClick={() => setVehicleType(type)}
                  className={`px-4 py-3 rounded-lg font-medium transition-all ${
                    vehicleType === type
                      ? 'bg-blue-500 text-white shadow-lg transform scale-105'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          {/* Calculate Button */}
          <button
            onClick={handleCalculate}
            disabled={loading || !distance}
            className="w-full bg-blue-500 text-white py-3 rounded-lg font-medium hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all flex items-center justify-center space-x-2"
          >
            {loading ? (
              <>
                <Loader className="w-5 h-5 animate-spin" />
                <span>계산 중...</span>
              </>
            ) : (
              <span>{t.calculate}</span>
            )}
          </button>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              <div className="font-medium">{t.error}</div>
              <div className="text-sm mt-1">{error}</div>
            </div>
          )}

          {/* Notice (e.g. 10km 미만 안내) */}
          {notice && (
            <div className="bg-blue-50 border border-blue-200 text-blue-800 px-4 py-3 rounded-lg">
              <div className="text-sm">{notice}</div>
            </div>
          )}

          {/* Result */}
          {price && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-6">
              <div className="text-green-800 font-bold text-lg mb-3">{t.result}</div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.vehicleType}:</span>
                  <span className="font-semibold">{price.vehicle_type}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">{t.distance}:</span>
                  <span className="font-semibold">{price.distance} km</span>
                </div>
                {price.rounded_distance !== price.distance && (
                  <div className="flex justify-between text-sm text-gray-500">
                    <span>{t.roundedDistance}:</span>
                    <span>{price.rounded_distance} km</span>
                  </div>
                )}
                <div className="pt-3 mt-3 border-t border-green-200">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">{t.price}:</span>
                    <span className="text-2xl font-bold text-green-600">
                      {price.price_formatted}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TariffCalculator;
